import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import {
  Grid,
  Container,
  Typography,
  TextField,
  TablePagination,
  Checkbox,
  Box,
  Card,
  Chip,
  IconButton,
  Link as MuiLink
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { QRCodeSVG } from 'qrcode.react';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PixIcon from '@mui/icons-material/Pix';

import List from 'components/List';
import { useDialog } from 'components/Dialog';

import { getChargesDashboard, toggleChargePerformed } from 'services/requests/dashboard';
import { chargeInstallments } from 'services/requests/contract';
import { setMask, toast } from 'utils';

export default function ChargesDashboard() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const dialog = useDialog();

  const [charges, setCharges] = useState({ data: [], pagination: {}, totalValue: 0 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');

  const getData = (currentPage = 1, search = '') => {
    setLoading(true);
    getChargesDashboard({ page: currentPage, searchTerm: search })
      .then((res) => setCharges(res))
      .catch((err) => toast(err, 'error'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setPage(1);
      getData(1, searchTerm);
    }, 500);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  useEffect(() => {
    getData(page, searchTerm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const handleToggleCharge = async (contractId, currentValue, e) => {
    e.stopPropagation();
    const newValue = !currentValue;
    
    try {
      await toggleChargePerformed({ contractId, performed: newValue });
      setCharges(prev => ({
        ...prev,
        data: prev.data.map(item => 
          item.id === contractId 
            ? { ...item, charge_performed: newValue }
            : item
        )
      }));
      toast(newValue ? 'Cobrança marcada como realizada' : 'Cobrança desmarcada', 'success');
    } catch (err) {
      toast(err, 'error');
    }
  };

  const handleGenerateCharge = (contract, e) => {
    e.stopPropagation();
    
    if (!contract.oldest_pending_installment_id) {
      toast('Não há parcela pendente para gerar cobrança', 'error');
      return;
    }

    dialog({
      title: 'Gerar cobrança',
      description: `Deseja gerar a cobrança da parcela mais antiga (${setMask('money', contract.oldest_pending_value)})?`,
    })
      .then(() => {
        chargeInstallments({ body: { installments: [contract.oldest_pending_installment_id] } })
          .then((res) => {
            window.navigator.clipboard.writeText(res.openPixBrCode);
            dialog({
              title: 'Cobrança PIX gerada',
              hideCancel: true,
              confirmationText: 'Ok',
              content: (
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="body1" sx={{ mb: 2, fontWeight: 'bold' }}>
                    Valor: {setMask('money', res.value)}
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                    <QRCodeSVG value={res.openPixBrCode} size={200} />
                  </Box>
                  <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                    Código PIX copiado para área de transferência
                  </Typography>
                </Box>
              ),
            })
              .then(() => {
                getData(page, searchTerm);
              })
              .catch(() => {});
          })
          .catch((err) => toast(err, 'error'));
      })
      .catch(() => {});
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'primary';
      case 'overdue':
        return 'error';
      case 'due_today':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'paid':
        return 'Pago';
      case 'overdue':
        return 'Atrasado';
      case 'due_today':
        return 'Vence hoje';
      default:
        return 'Em aberto';
    }
  };

  const getRowColor = (status) => {
    switch (status) {
      case 'paid':
        return { color: 'primary.main' };
      case 'overdue':
        return { color: 'error.main' };
      default:
        return {};
    }
  };

  const totalDelayedValue = charges.totalValue || 0;

  const sortedData = [...(charges.data || [])].sort((a, b) => {
    if (a.paid_today && !b.paid_today) return 1;
    if (!a.paid_today && b.paid_today) return -1;
    if (a.status === 'overdue' && b.status !== 'overdue') return -1;
    if (a.status !== 'overdue' && b.status === 'overdue') return 1;
    return 0;
  });

  const listConfig = {
    onClick: () => null,
    disabledRow: row => row.status === 'paid' || row.paid_today,
    fields: [
      {
        key: 'number',
        label: 'CT/PC',
        desktopColumnSize: 1.5,
        sm_prefix: 'Contrato: ',
        customValue: row => (
          <MuiLink
            href={row.contract_link}
            onClick={(e) => {
              e.stopPropagation();
              navigate(row.contract_link);
            }}
            sx={{ 
              fontWeight: 'bold',
              color: row.status === 'paid' ? 'primary.main' : row.status === 'overdue' ? 'error.main' : 'text.primary',
              fontSize: '0.9rem'
            }}
          >
            {row.number}-{row.paid_installments}/{row.total_installments}
          </MuiLink>
        )
      },
      {
        key: 'client_name',
        label: 'Cliente',
        desktopColumnSize: 3,
        sm_prefix: 'Cliente: ',
        customValue: row => (
          <Typography
            as="span"
            sx={{ 
              color: row.status === 'paid' ? 'primary.main' : row.status === 'overdue' ? 'error.main' : 'text.primary',
              fontWeight: 'medium',
              fontSize: '0.9rem'
            }}
          >
            {row.client_name}
          </Typography>
        )
      },
      {
        key: 'oldest_pending_value',
        label: 'Valor',
        desktopColumnSize: 1.5,
        sm_prefix: 'Valor: ',
        customValue: row => {
          if (row.status === 'paid') {
            return (
              <Typography as="span" sx={{
                color: 'primary.main', 
                fontWeight: 'bold',
                fontSize: '0.95rem'
              }}>
                {setMask('money', row.oldest_pending_value || 0)}
              </Typography>
            );
          }
          return (
            <MuiLink
              href="#"
              onClick={(e) => handleGenerateCharge(row, e)}
              sx={{ 
                display: 'inline-block',
                fontWeight: 'bold', 
                color: 'primary.main',
                fontSize: '0.95rem',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              {setMask('money', row.oldest_pending_value || 0)}
            </MuiLink>
          );
        }
      },
      // {
      //   key: 'status',
      //   label: 'Status',
      //   desktopColumnSize: 1.3,
      //   sm_prefix: 'Status: ',
      //   customValue: row => (
      //     <Chip
      //       label={getStatusLabel(row.status)}
      //       color={getStatusColor(row.status)}
      //       size="small"
      //       sx={{ 
      //         fontSize: '0.75rem',
      //         height: '24px'
      //       }}
      //     />
      //   )
      // },
      {
        key: 'observation',
        label: 'Observação',
        desktopColumnSize: 2,
        mobileHidden: true,
        sm_prefix: 'Obs: ',
        customValue: row => (
          <Typography sx={{ 
            color: row.status === 'paid' ? 'primary.main' : row.status === 'overdue' ? 'error.main' : 'text.primary',
            fontSize: '0.85rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {row.observation || '-'}
          </Typography>
        )
      },
      {
        key: 'last_payment_date',
        label: 'Último pagamento',
        desktopColumnSize: 1,
        sm_prefix: 'Último pagamento: ',
        customValue: row => (
          <Typography as="span" sx={{ 
            color: row.status === 'paid' ? 'primary.main' : row.status === 'overdue' ? 'error.main' : 'text.primary',
            fontSize: '0.85rem'
          }}>
            {row.last_payment_date
              ? format(new Date(row.last_payment_date), 'dd/MM/yyyy')
              : '-'}
          </Typography>
        )
      },
      {
        key: 'whatsapp',
        label: 'Cobrar',
        desktopColumnSize: 1.5,
        sm_prefix: '',
        customValue: row => {
          if (row.paid_today || row.status === 'paid') {
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                <CheckCircleIcon sx={{ color: 'primary.main', fontSize: '1.3rem' }} />
              </Box>
            );
          }
          return (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              width: '100%',
              pt: 1,
              gap: 0.5,
              flexWrap: 'wrap'
            }}>
              <Box>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(row.whatsapp_link, '_blank');
                  }}
                  sx={{ 
                    color: 'success.main',
                    '&:hover': {
                      bgcolor: 'success.lighter',
                      transform: 'scale(1.1)'
                    },
                    transition: 'all 0.2s'
                  }}
                  title="Abrir WhatsApp"
                >
                  <WhatsAppIcon />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={(e) => handleGenerateCharge(row, e)}
                  sx={{ 
                    color: 'primary.main',
                    '&:hover': {
                      bgcolor: 'primary.lighter',
                      transform: 'scale(1.1)'
                    },
                    transition: 'all 0.2s',
                    ml: { xs: 2, md: 0 }
                  }}
                  title="Gerar cobrança PIX"
                  disabled={!row.oldest_pending_installment_id}
                >
                  <PixIcon />
                </IconButton>
              </Box>
              <Box sx={{ display: { xs: 'flex', md: 'none', alignItems: 'center' } }}>
                <Typography as="span" variant="body2" sx={{ fontSize: '0.85rem' }}>Cobrança realizada</Typography>
                <Checkbox
                  checked={row.charge_performed || false}
                  onChange={(e) => handleToggleCharge(row.id, row.charge_performed, e)}
                  onClick={(e) => e.stopPropagation()}
                  size="small"
                  sx={{
                    '& .MuiSvgIcon-root': {
                      fontSize: '1.2rem'
                    }
                  }}
                />
              </Box>
            </Box>
          );
        }
      },
      {
        key: 'charge_performed',
        label: 'Cobrado',
        desktopColumnSize: 1.5,
        mobileHidden: true,
        sm_prefix: 'Cobrança realizada: ',
        customValue: row => {
          if (row.paid_today || row.status === 'paid') {
            return null;
          }
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Checkbox
                checked={row.charge_performed || false}
                onChange={(e) => handleToggleCharge(row.id, row.charge_performed, e)}
                onClick={(e) => e.stopPropagation()}
                size="small"
                sx={{
                  '& .MuiSvgIcon-root': {
                    fontSize: '1.2rem'
                  }
                }}
              />
            </Box>
          );
        }
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>Cobranças | {process.env.REACT_APP_PROJECT_NAME}</title>
      </Helmet>

      <Container>
        <Grid container rowSpacing={2} columnSpacing={2} my={1}>
          <Grid item xs={12}>
            <Card sx={{ 
              p: { xs: 2, sm: 3 }, 
              bgcolor: 'error.main', 
              color: 'white',
              borderRadius: 2
            }}>
              <Typography 
                variant="h4" 
                sx={{ 
                  fontWeight: 'bold',
                  fontSize: { xs: '1.5rem', sm: '2rem' },
                  textAlign: { xs: 'center', sm: 'left' }
                }}
              >
                TOTAL DO DIA: {setMask('money', totalDelayedValue)}
              </Typography>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              placeholder="Buscar contrato ou cliente..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ my: 2, backgroundColor: 'white' }}
              size="small"
            />
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ 
              '& .MuiListItemButton-root': {
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: { xs: 'flex-start', md: 'center' },
                py: { xs: 2, md: 1.5 },
                px: { xs: 2, md: 2 },
                '&:hover': {
                  bgcolor: { md: 'action.hover' }
                },
                transition: 'background-color 0.2s'
              },
              '& .MuiListItemText-secondary': {
                mt: { xs: 1, md: 0 }
              },
              '& .MuiGrid-container': {
                alignItems: 'center'
              },
              '& .MuiGrid-item': {
                display: 'flex',
                alignItems: 'center',
                minHeight: '40px'
              },
              '& .MuiListItem': {
                '&:first-of-type': {
                  '& .MuiTypography-root': {
                    fontWeight: 600,
                    fontSize: '0.875rem'
                  }
                }
              }
            }}>
              <List
                config={listConfig}
                data={sortedData}
                refresh={() => getData(page, searchTerm)}
                loading={loading}
                showPagination={false}
                showInput={false}
                showAvatar={false}
              />
            </Box>
            
            {charges.pagination && (
              <Box sx={{ 
                display: 'flex',
                justifyContent: 'flex-end',
                mt: 2,
                overflow: 'auto',
                '& .MuiTablePagination-toolbar': {
                  flexWrap: 'wrap',
                  gap: 1
                }
              }}>
                <TablePagination
                  component="div"
                  count={charges.pagination.total || 0}
                  rowsPerPage={20}
                  page={(page || 1) - 1}
                  onPageChange={(e, newPage) => setPage(newPage + 1)}
                  rowsPerPageOptions={[]}
                  labelDisplayedRows={({ from, to, count }) =>
                    `${from}–${to} de ${count !== -1 ? count : `mais de ${to}`}`
                  }
                  labelRowsPerPage=""
                  sx={{
                    '& .MuiTablePagination-displayedRows': {
                      fontSize: { xs: '0.75rem', sm: '0.875rem' }
                    }
                  }}
                />
              </Box>
            )}
          </Grid>
        </Grid>
      </Container>
    </>
  );
}

